import type { RequestLogger } from "./logger";

const isServer = globalThis.window === undefined;
const clientLogLevel = (process.env.NEXT_PUBLIC_LOG_LEVEL ?? "INFO").toUpperCase();

type NodeRequireLike = (id: string) => unknown;

type GlobalLoggerState = typeof globalThis & {
  __sharedUtilsServerLogger?: RequestLogger;
};

let serverLogger: RequestLogger | null = null;
let loggerLoadAttempted = false;
let lastLoggerLoadFailureAt = 0;

const LOGGER_RETRY_INTERVAL_MS = 10_000;

const isLoggingEnabled = (): boolean => process.env.NEXT_PUBLIC_ENABLE_LOGGING === "true";

const isServerLoggingEnabled = (): boolean => isLoggingEnabled();

const isClientLoggingEnabled = (): boolean => isLoggingEnabled();

const resolveRuntimeRequire = (): NodeRequireLike | null => {
  if (!isServer) return null;

  const nodeProcess = process as NodeJS.Process & {
    getBuiltinModule?: (id: string) => unknown;
  };

  // Node >=20: preferred server-safe path without bundling static imports.
  try {
    const moduleBuiltin = nodeProcess.getBuiltinModule?.("module") as
      | { createRequire?: (filename: string) => NodeRequireLike }
      | undefined;
    if (typeof moduleBuiltin?.createRequire === "function") {
      const fromPath =
        typeof __filename === "string" && __filename.length > 0
          ? __filename
          : `${process.cwd()}/packages/shared-utils/src/utils/log-utils.ts`;
      return moduleBuiltin.createRequire(fromPath);
    }
  } catch {
    // continue to fallback resolvers
  }

  const runtimeGlobal = globalThis as typeof globalThis & {
    __non_webpack_require__?: NodeRequireLike;
    require?: NodeRequireLike;
  };

  if (runtimeGlobal.__non_webpack_require__) {
    return runtimeGlobal.__non_webpack_require__;
  }
  if (runtimeGlobal.require) {
    return runtimeGlobal.require;
  }

  try {
    // eslint-disable-next-line no-new-func
    return new Function("return require")() as NodeRequireLike;
  } catch {
    return null;
  }
};

const getLogger = (): RequestLogger | null => {
  if (!isServer) return null;
  if (serverLogger) return serverLogger;

  const globalLogger = (globalThis as GlobalLoggerState).__sharedUtilsServerLogger;
  if (globalLogger) {
    serverLogger = globalLogger;
    lastLoggerLoadFailureAt = 0;
    return serverLogger;
  }

  const now = Date.now();
  if (loggerLoadAttempted && now - lastLoggerLoadFailureAt < LOGGER_RETRY_INTERVAL_MS) {
    return null;
  }

  loggerLoadAttempted = true;

  const runtimeRequire = resolveRuntimeRequire();
  if (!runtimeRequire) {
    lastLoggerLoadFailureAt = now;
    return null;
  }

  try {
    const loggerModule = runtimeRequire("@repo/shared-utils/logger") as {
      logger?: RequestLogger;
    };
    serverLogger = loggerModule.logger ?? null;
    if (serverLogger) {
      lastLoggerLoadFailureAt = 0;
      (globalThis as GlobalLoggerState).__sharedUtilsServerLogger = serverLogger;
    }
    return serverLogger;
  } catch {
    try {
      const loggerModule = runtimeRequire("./logger") as {
        logger?: RequestLogger;
      };
      serverLogger = loggerModule.logger ?? null;
      if (serverLogger) {
        lastLoggerLoadFailureAt = 0;
        (globalThis as GlobalLoggerState).__sharedUtilsServerLogger = serverLogger;
      }
      return serverLogger;
    } catch {
      lastLoggerLoadFailureAt = now;
      return null;
    }
  }
};

const clientLevelEnabled = (level: "ERROR" | "DEBUG" | "INFO"): boolean => {
  if (clientLogLevel === "DEBUG") return true;
  if (clientLogLevel === "INFO") return level !== "DEBUG";
  return level === "ERROR";
};

const normalizeFallbackValue = (value: unknown, seen = new WeakSet<object>()): unknown => {
  if (value == null || typeof value === "string" || typeof value === "number") {
    return value;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeFallbackValue(entry, seen));
  }
  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
      (acc, [key, entry]) => {
        acc[key] = normalizeFallbackValue(entry, seen);
        return acc;
      },
      {}
    );
  }
  if (typeof value === "symbol") {
    return value.toString();
  }
  if (typeof value === "function") {
    return `[Function ${value.name || "anonymous"}]`;
  }
  return Object.prototype.toString.call(value);
};

const writeStructuredFallbackLog = (level: "debug" | "info" | "error", args: unknown[]): void => {
  const message = typeof args[0] === "string" ? args[0] : "";
  let payloadToNormalize: unknown;
  if (args.length <= 1) {
    payloadToNormalize = args[0];
  } else if (typeof args[0] === "string") {
    payloadToNormalize = args.slice(1);
  } else {
    payloadToNormalize = args;
  }
  const data = normalizeFallbackValue(payloadToNormalize);
  let levelValue = 30;
  if (level === "error") {
    levelValue = 50;
  } else if (level === "debug") {
    levelValue = 20;
  }

  const line = JSON.stringify({
    level: levelValue,
    time: new Date().toISOString(),
    service: process.env.SERVICE_NAME ?? "next-app",
    env: process.env.NODE_ENV ?? "development",
    message,
    data,
    loggerFallback: true,
    loggerLoadAttempted,
    nextRetryInMs:
      lastLoggerLoadFailureAt > 0
        ? Math.max(0, LOGGER_RETRY_INTERVAL_MS - (Date.now() - lastLoggerLoadFailureAt))
        : 0,
  });

  if (level === "error") {
    process.stderr.write(`${line}\n`);
    return;
  }
  process.stdout.write(`${line}\n`);
};

const logWithPino = (level: "debug" | "info" | "error", args: unknown[]): void => {
  const logger = getLogger();
  if (!logger) {
    writeStructuredFallbackLog(level, args);
    return;
  }
  if (args.length === 0) return;

  if (typeof args[0] === "string") {
    const message = args[0];
    const data = args.slice(1);
    logger[level]({ data: data.length === 1 ? data[0] : data }, message);
    return;
  }

  logger[level]({ data: args.length === 1 ? args[0] : args }, "");
};

export const LogUtils = {
  error: (...args: unknown[]) => {
    if (args.length === 0) return;
    if (isServer) {
      if (!isServerLoggingEnabled()) return;
      logWithPino("error", args);
      return;
    }
    if (!isClientLoggingEnabled() || !clientLevelEnabled("ERROR")) return;
    console.log(...args);
  },

  debug: (...args: unknown[]) => {
    if (args.length === 0) return;
    if (isServer) {
      if (!isServerLoggingEnabled()) return;
      logWithPino("debug", args);
      return;
    }
    if (!isClientLoggingEnabled() || !clientLevelEnabled("DEBUG")) return;
    console.log(...args);
  },

  log: (...args: unknown[]) => {
    if (args.length === 0) return;
    if (isServer) {
      if (!isServerLoggingEnabled()) return;
      logWithPino("info", args);
      return;
    }
    if (!isClientLoggingEnabled() || !clientLevelEnabled("INFO")) return;
    console.log(...args);
  },
};
